/**
 * Parken: die eigenen Stellplätze als Zusammenfassung — gepflegt auf der Seite „Parkplätze“ — und
 * die Alternativen in der Umgebung.
 *
 * Die Alternativen liegen am Objekt, nicht am Apartment: wo man in der Straße sonst noch stehen
 * kann, ist für alle Wohnungen im Haus dieselbe Auskunft. Sie benutzen dieselbe Mechanik wie die
 * Anfahrt — Reihenfolge, Bilder mit Markierungen, Vererbung ins Apartment —, weil dieselbe Arbeit
 * ein zweites Mal zu bauen hieße, jeden künftigen Fix zweimal zu machen.
 */
import { useQuery } from '@tanstack/react-query';
import { useApi, useApiProblem } from '@/shared/api';
import { ArrivalGuideEditor } from '@/features/properties/arrival-guide';
import { useObjectDetail } from './ObjectDetailContext';
import { ObjectParkingSummary } from './ObjectParkingSummary';
import { ParkingPreviewLead } from './ParkingPreviewLead';
import { parkingAccessDenied, parkingProblemText } from '../parking-occupancy/parkingProblemText';

export function ObjectParkingTab() {
  const { groupId, canEdit } = useObjectDetail();
  const api = useApi();
  const parking = useQuery({
    queryKey: ['property-groups', groupId, 'parking-spots'],
    queryFn: () => api.parking.listSpots(groupId),
  });
  const problem = useApiProblem(parking.error ?? null);
  const denied = parkingAccessDenied(problem);

  return (
    <section aria-labelledby="object-parking-title" className="space-y-5">
      <div>
        <h2 id="object-parking-title" className="text-ink font-serif text-2xl">
          Parken
        </h2>
        <p className="text-ink-soft mt-1 max-w-3xl text-sm">
          Eigene Stellplätze und Alternativen in der Nähe. Beides steht im Parkbereich des
          Gastportals – die Alternativen besonders dann, wenn kein Stellplatz mehr frei ist.
        </p>
      </div>
      <ObjectParkingSummary
        groupId={groupId}
        parking={parking.data}
        failed={parking.isError}
        failedReason={denied ? parkingProblemText(problem, '') : undefined}
        onRetry={denied ? undefined : () => void parking.refetch()}
      />
      <div className="space-y-2">
        <h3 className="text-ink font-serif text-lg">Alternativen in der Nähe</h3>
        <ArrivalGuideEditor
          scope={{ kind: 'object', id: groupId, guide: 'ParkingAlternatives' }}
          canEdit={canEdit}
          previewLead={(locale) => (
            <ParkingPreviewLead parking={parking.data} failed={parking.isError} locale={locale} />
          )}
        />
      </div>
    </section>
  );
}
